/**
 * Pure helpers for the Action Center: wire -> view-model mapping, severity ordering,
 * and the immediate-attention / per-category partition.
 */

import type { Notification } from '@amzn/coral_com-amazon-jwonotificationservice/Notification'
import {
    ActionCenterPriority,
    IActionCenterAction,
    IActionCenterNotification,
    ICategoryGroup,
    KnownCategory,
} from './types'
import { IMMEDIATE_ATTENTION_PRIORITIES, KNOWN_CATEGORIES, PRIORITY_RANK } from './constants'

const isPriority = (value: unknown): value is ActionCenterPriority =>
    typeof value === 'string' && value in PRIORITY_RANK

export const isKnownCategory = (value: string | undefined): value is KnownCategory =>
    !!value && (KNOWN_CATEGORIES as ReadonlyArray<string>).includes(value)

/**
 * Flattens a wire `Notification` into the card view-model. Returns null for rows the UI cannot
 * render at all (missing id, unknown priority, or no createdAt), so one malformed row does not
 * blank the whole inbox.
 */
export function toActionCenterNotification (n: Notification): IActionCenterNotification | null {
    if (!n?.notificationId || !isPriority(n.priority) || !n.createdAt) {
        return null
    }
    const createdAt = n.createdAt instanceof Date ? n.createdAt : new Date(n.createdAt)
    if (Number.isNaN(createdAt.getTime())) {
        return null
    }
    const actions: IActionCenterAction[] = (n.content?.actions ?? [])
        .filter((a) => !!a?.label)
        .map((a) => ({ label: a.label, deepUrl: a.deepUrl || undefined, toolName: a.toolName || undefined }))

    return {
        notificationId: n.notificationId,
        status: n.status,
        priority: n.priority,
        category: n.category || undefined,
        notificationType: n.notificationType || undefined,
        storeId: n.storeId || undefined,
        title: n.title || n.notificationType || '',
        body: n.body || undefined,
        summary: n.content?.summary || undefined,
        details: n.content?.details || undefined,
        location: n.content?.location || undefined,
        actions,
        createdAt,
    }
}

/** READ is the terminal inbox state; everything else (UNREAD, INTERACTED, ...) is still open. */
export const isRead = (n: IActionCenterNotification): boolean => n.status === 'READ'

/**
 * Open work in a list: the items not yet READ. Every number the Action Center shows (tab
 * badges, the "Needs immediate attention" badge, the category rows) is this, never the list
 * length, so acknowledged items can stay listed as history without being counted as tasks.
 */
export const countUnread = (notifications: IActionCenterNotification[]): number =>
    notifications.filter((n) => !isRead(n)).length

/**
 * Inbox order: unread before read, then severity (CRITICAL -> LOW), then newest first.
 * Stable; does not mutate. On an unread-only list this is a pure severity sort.
 */
export function sortNotifications (notifications: IActionCenterNotification[]): IActionCenterNotification[] {
    return [...notifications].sort((a, b) => {
        const read = Number(isRead(a)) - Number(isRead(b))
        if (read !== 0) return read
        const rank = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority]
        if (rank !== 0) return rank
        return b.createdAt.getTime() - a.createdAt.getTime()
    })
}

export function needsImmediateAttention (n: IActionCenterNotification): boolean {
    return IMMEDIATE_ATTENTION_PRIORITIES.includes(n.priority)
}

/**
 * Groups everything below the immediate-attention bar by category. Known categories come first
 * in their fixed order, then any other category alphabetically. Empty groups are omitted.
 */
export function groupByCategory (notifications: IActionCenterNotification[]): ICategoryGroup[] {
    const byCategory = new Map<string, IActionCenterNotification[]>()
    for (const n of notifications) {
        const key = n.category ?? ''
        const bucket = byCategory.get(key) ?? []
        bucket.push(n)
        byCategory.set(key, bucket)
    }
    const known = KNOWN_CATEGORIES.filter((c) => byCategory.has(c))
    const other = [...byCategory.keys()]
        .filter((c) => !isKnownCategory(c))
        .sort((a, b) => a.localeCompare(b))
    return [...known, ...other].map((category) => ({
        category,
        notifications: sortNotifications(byCategory.get(category) ?? []),
    }))
}
